import { FC, useMemo } from 'react';
import { FundInvestmentsContext, FundInvestmentsContextValue } from 'context';
import {
  useFundInvestmentAllocationStatus,
  useFundInvestmentAllocationVersion,
  useFundInvestmentSpreadsheet,
  useFundInvestmentValuationAllocation,
  useFundInvestmentValuationOwnership,
  useFundInvestmentValuationReadyForAudit,
} from 'pages/FundInvestments/hooks';
import { FundInvestmentsProviderProps } from './FundInvestmentsProvider.props';

const FundInvestmentsProvider: FC<FundInvestmentsProviderProps> = props => {
  const { children } = props;

  const fundInvestmentSpreadsheet = useFundInvestmentSpreadsheet();
  const {
    areAllCellsValid,
    cleanUnsavedChanges,
    currentAsOfDate,
    currentDeletedMarketAdjustments,
    currentDeletedMarketComparisons,
    currentMarketAdjustment,
    currentMarketComparisons,
    setArePendingChanges,
    setHasValidationErrors,
    setIsValidating,
  } = fundInvestmentSpreadsheet;

  const fundInvestmentValuationAllocation = useFundInvestmentValuationAllocation();
  const {
    allocationVersionDialogProps,
    currentAllocation,
    currentAllocationVersion,
    currentMeasurementDate,
    currentMeasurementDateId,
    getValuationByAllocation,
    isUpdatingValuation,
    marketCurrency,
    marketDate,
    updateValuation,
    valuationByAllocation,
  } = fundInvestmentValuationAllocation;

  const fundInvestmentAllocationVersion = useFundInvestmentAllocationVersion({
    allocationVersionDialogProps,
    areAllCellsValid,
    cleanUnsavedChanges,
    currentAllocation,
    currentAsOfDate,
    currentDeletedMarketAdjustments,
    currentDeletedMarketComparisons,
    currentMarketAdjustment,
    currentMarketComparisons,
    currentMeasurementDate,
    currentMeasurementDateId,
    marketCurrency,
    marketDate,
    setArePendingChanges,
    setHasValidationErrors,
    setIsValidating,
    valuationByAllocation,
  });

  const fundInvestmentValuationOwnership = useFundInvestmentValuationOwnership({
    updateValuation,
    valuationByAllocation,
  });
  const { isCurrentUserAuditor, isCurrentValuationFirmOwned } = fundInvestmentValuationOwnership;

  const fundInvestmentAllocationStatus = useFundInvestmentAllocationStatus({
    currentAllocationVersion,
    getValuationByAllocation,
    isCurrentValuationFirmOwned,
    isUpdatingValuation,
    isCurrentUserAuditor,
  });

  const fundInvestmentValuationReadyForAudit = useFundInvestmentValuationReadyForAudit({
    valuationByAllocation,
    getValuationByAllocation,
  });

  const contextValue = useMemo<FundInvestmentsContextValue>(
    () => ({
      fundInvestmentAllocationStatus,
      fundInvestmentAllocationVersion,
      fundInvestmentSpreadsheet,
      fundInvestmentValuationAllocation,
      fundInvestmentValuationOwnership,
      fundInvestmentValuationReadyForAudit,
    }),
    [
      fundInvestmentAllocationStatus,
      fundInvestmentAllocationVersion,
      fundInvestmentSpreadsheet,
      fundInvestmentValuationAllocation,
      fundInvestmentValuationOwnership,
      fundInvestmentValuationReadyForAudit,
    ]
  );

  return <FundInvestmentsContext.Provider value={contextValue}>{children}</FundInvestmentsContext.Provider>;
};

export default FundInvestmentsProvider;
